import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import React, {useState} from 'react';
import type {RegionRequest, RegionResponse} from 'src/api/ProactApiClient';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {SidebarLinkProjectsAdmin} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {
  useCreateRegionMutation,
  useGetRegionQuery,
  useUpdateRegionMutation,
} from 'src/redux/queries/region';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {BackBar} from 'src/app/layout/BackBar';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {
  MapRegionResponseToRegionRequest,
  RegionDocumentTitle,
  RegionFormMeta,
} from './regionHelpers';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface RegionFormProps {
  id?: number;
  mode: 'create' | 'edit';
}

export const RegionForm: React.FC<RegionFormProps> = ({id, mode}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [isSavePending, setIsSavePending] = useState(false);

  const isCreateMode = mode === 'create';
  const isEditMode = mode === 'edit';

  const {data: fetchedRegion, isLoading: isRegionLoading} = useGetRegionQuery(
    id!,
    {skip: !isEditMode || id === undefined}
  );

  const [createRegion] = useCreateRegionMutation();
  const [updateRegion] = useUpdateRegionMutation();

  const isLoading = isEditMode && isRegionLoading;

  const form = useEntitySyncedForm({
    formMeta: RegionFormMeta,
    entity: fetchedRegion,
    toFormData: toFormDataV2,
  });

  const listPath = PathUtility.Project('regions');

  const handleSave = async () => {
    setIsSavePending(true);

    try {
      const values = form.getFieldValues();
      const model: RegionRequest = MapRegionResponseToRegionRequest({
        ...values,
        id: fetchedRegion?.id,
      } as RegionResponse);

      if (isCreateMode) {
        const response = await createRegion(model).unwrap();
        setAlert(`${response.name} was successfully created`, 'success');
      } else {
        await updateRegion(model).unwrap();
        setAlert(
          `${fetchedRegion?.name ?? 'Region'} was successfully updated`,
          'success'
        );
      }

      navigate(listPath, {replace: true});
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsSavePending(false);
  };

  const handleValidateAndSave = async () => {
    const isValid = await form.validateForm();
    if (isValid) {
      void handleSave();
    }
  };

  const {goBack: handleCancel} = useBackNavigation(listPath);

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  const pageTitle = isCreateMode
    ? 'New Region'
    : fetchedRegion?.name ?? 'Edit Region';

  const saveButtons = (
    <>
      <PageHeader.Action variant="cancel" onClick={handleCancel}>
        Cancel
      </PageHeader.Action>
      <PageHeader.Action
        variant={isCreateMode ? 'create' : 'save'}
        onClick={handleValidateAndSave}
        loading={isSavePending}>
        {isCreateMode ? 'Create Region' : 'Save Changes'}
      </PageHeader.Action>
    </>
  );

  return (
    <Stack>
      <BackBar
        fallbackTo={listPath}
        fallbackLabel="Back to Regions"
        breadcrumbs={[
          {label: 'Projects', to: PathUtility.Project('list')},
          {label: RegionDocumentTitle, to: listPath},
          {label: isCreateMode ? 'New Region' : 'Edit'},
        ]}
      />

      <PageHeader>
        <PageHeader.Title icon={SidebarLinkProjectsAdmin.icon}>
          {pageTitle}
        </PageHeader.Title>

        {isEditMode && (
          <PageHeader.Badges>
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}

        <PageHeader.Actions>{saveButtons}</PageHeader.Actions>
      </PageHeader>

      <ProactForm form={form}>
        <ProactFormGrid />
      </ProactForm>

      <Group>{saveButtons}</Group>
    </Stack>
  );
};
